
import { Movie, PendingAction } from "@/types/movie";
import { ActionHandlers } from "./types";
import { createPendingAction, hasPendingExpired } from "./pendingActionUtils";
import { LIST_NAMES, SINGLE_LIST_TYPE } from "@/hooks/search/types/listConstants";
import { SingleListService } from "@/services/movie/singleListService";

/**
 * Toggles a movie's favorite status using single-list operations
 * FIXED: Unseen Gems always shows undo when adding to favorites for consistency
 */
export const toggleMovieFavorite = async (
  movie: Movie,
  currentSection: string | undefined,
  pendingActions: PendingAction[],
  handlers: ActionHandlers
): Promise<void> => {
  if (!movie.tmdbId) {
    console.error("❌ Cannot toggle favorite for movie without tmdbId:", movie);
    return;
  }

  console.log(`💎 [FAVORITE] toggleMovieFavorite called:`, {
    title: movie.title,
    tmdbId: movie.tmdbId,
    currentFavorite: movie.favorite,
    currentSection,
    willShowUndo: shouldShowUndo(movie, currentSection)
  });
  
  // Handle existing pending actions
  const existingAction = pendingActions.find(
    action => action.movieId === movie.tmdbId && action.type === 'favorite'
  );
  
  if (existingAction) {
    console.log(`💎 [FAVORITE] Found existing pending action - cancelling and applying immediately`);
    
    window.clearTimeout(existingAction.timeoutId);
    handlers.removePendingAction(existingAction.id);
    
    if (!hasPendingExpired(existingAction)) {
      const shouldBeFavorite = !existingAction.movieData.favorite;
      
      try {
        console.log(`💎 [FAVORITE] Executing immediate single-list operation for existing action cancel: ${movie.title} -> ${shouldBeFavorite}`);
        
        const result = shouldBeFavorite 
          ? await SingleListService.addToList(movie, SINGLE_LIST_TYPE.FAVORITE)
          : await SingleListService.removeFromList(movie.tmdbId, SINGLE_LIST_TYPE.FAVORITE);
        
        if (!result.success) {
          console.error('❌ [FAVORITE] Single-list operation failed (cancel action):', result.error);
          return;
        }
        
        console.log('✅ [FAVORITE] Single-list operation success: favorite status updated (cancel action)');
        
        const updatedMovie = {
          ...movie,
          favorite: shouldBeFavorite
        };
        
        handlers.updateMovieState(movie.tmdbId, updatedMovie);
        
      } catch (error) {
        console.error(`❌ [FAVORITE] Error setting favorite status for ${movie.title}:`, error);
        return;
      }
      return;
    }
  }
  
  // FIXED: Determine if undo should be shown for consistent behavior
  const shouldShowUndoAction = shouldShowUndo(movie, currentSection);
  
  if (shouldShowUndoAction) {
    console.log(`💎 [FAVORITE] Creating pending action for ${movie.title} (${movie.favorite ? 'removal from' : 'addition to'} favorites)`);
    const pendingAction = createPendingAction(
      movie, 
      'favorite', 
      currentSection || '',
      async (tmdbId, _, newFavoriteValue, enrichedMovie) => {
        console.log(`💎 [FAVORITE] Executing undo completion for ${movie.title}: ${newFavoriteValue}`);
        handlers.updateMovieState(tmdbId, enrichedMovie);
        console.log(`💎 [FAVORITE] Favorite status change completed for ${movie.title}`);
      },
      handlers.removePendingAction
    );
    
    handlers.addPendingAction(pendingAction);
  } else {
    // Apply immediately using single-list service
    try {
      const newFavoriteValue = !movie.favorite;
      console.log(`💎 [FAVORITE] Executing immediate toggle for ${movie.title}:`, {
        from: movie.favorite,
        to: newFavoriteValue,
        reason: 'immediate action context'
      });
      
      const result = newFavoriteValue 
        ? await SingleListService.addToList(movie, SINGLE_LIST_TYPE.FAVORITE)
        : await SingleListService.removeFromList(movie.tmdbId, SINGLE_LIST_TYPE.FAVORITE);
      
      if (!result.success) {
        console.error('❌ [FAVORITE] Single-list operation failed (immediate):', result.error);
        return;
      }
      
      console.log('✅ [FAVORITE] Single-list operation success: favorite status updated (immediate)', {
        tmdbId: movie.tmdbId,
        newFavoriteValue
      });
      
      const updatedMovie = {
        ...movie,
        favorite: newFavoriteValue
      };
      
      handlers.updateMovieState(movie.tmdbId, updatedMovie);
      
    } catch (error) {
      console.error(`❌ [FAVORITE] Error toggling favorite status for ${movie.title}:`, error);
    }
  }
};

/**
 * FIXED: Determine if undo should be shown for consistent Unseen Gems behavior
 */
function shouldShowUndo(movie: Movie, currentSection?: string): boolean {
  // Show undo when removing from favorites list
  if (movie.favorite && currentSection === LIST_NAMES.FAVORITES) {
    return true;
  }
  
  // FIXED: Always show undo when adding to favorites from Unseen Gems for consistency
  if (!movie.favorite && currentSection === LIST_NAMES.UNSEEN_GEMS) {
    return true;
  }
  
  return false;
}
